<template>
  <div :class="{ error: !!errorMessage }">
    <div
      class="star-rating-wrapper"
      :class="{ unrated: !model }"
      role="radiogroup"
      :aria-label="`${name} rating: ${model || 0} out of ${maxRating} stars`"
      :aria-required="validationRules ? 'true' : 'false'"
      :aria-invalid="!!errorMessage"
      :aria-describedby="errorMessage ? `${name}-error` : helpTextId"
      :tabindex="readOnly ? -1 : 0"
    >
      <div class="star-rating" @mouseleave="resetRating">
        <!-- Screen reader only label -->
        <span class="sr-only">{{ name }} rating</span>

        <!-- Radio buttons for accessibility -->
        <template v-for="n in maxRating" :key="n">
          <input
            :id="`${name}-star-${n}`"
            v-model.number="model"
            type="radio"
            :name="name"
            :value="n"
            class="star-rating-input sr-only"
            :disabled="readOnly"
            :aria-label="`${n} star${n !== 1 ? 's' : ''}`"
            @change="handleRatingChange(n)"
            @focus="currentRating = n"
            @blur="resetRating"
          />
          <label
            :for="`${name}-star-${n}`"
            class="star-rating-star"
            :class="{ 'star-rating-pointer': !readOnly }"
            @mouseenter="!readOnly && handleMouseEnter(n)"
            @touchstart="!readOnly && handleTouchStart()"
            @touchmove.passive="!readOnly && handleTouchMove()"
            @touchend="!readOnly && handleTouchEnd(n)"
            @touchcancel="!readOnly && handleTouchCancel()"
            @click.prevent="!isTouch && !readOnly && selectRating(n)"
          >
            <SingleStar
              :star-size="starSize"
              :star-id="n"
              :fill="starFill(n)"
            />
            <span class="sr-only"> {{ n }} star{{ n !== 1 ? 's' : '' }} </span>
          </label>
        </template>

        <!-- Pointer slot for additional UI elements (like tooltips) -->
        <slot name="pointer" />
      </div>
    </div>

    <!-- Error message with proper ID for ARIA -->
    <client-only>
      <transition name="fade">
        <div v-if="errorMessage" :id="`${name}-error`" role="alert">
          <slot name="errorMessage" :error-message="errorMessage" />
        </div>
      </transition>
    </client-only>

    <!-- Help text with proper ID for ARIA -->
    <div v-if="$slots.helpText" :id="helpTextId">
      <slot name="helpText" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { useField } from 'vee-validate'
import { ZodSchema } from 'zod'
import SingleStar from './SingleStar.vue'

const props = withDefaults(
  defineProps<{
    starSize: number
    name: string
    maxRating?: number
    activeColor?: string
    inactiveColor?: string
    readOnly?: boolean
    validationRules?: ZodSchema | undefined
  }>(),
  {
    maxRating: 10,
    activeColor: '#ffd055',
    inactiveColor: '#d8d8d8',
    readOnly: false,
    validationRules: undefined,
  },
)

const model = defineModel<number>({ required: true, default: 0 })

const emit = defineEmits(['rating-selected'])

const currentRating = ref(0)
const helpTextId = computed(() => `${props.name}-help`)
const isTouch = ref(false)

// Touch gesture tracking to prevent accidental scroll selections
let touchMoved = false

const name = toRef(props, 'name')
const validationRulesTypedSchema = computed(() => {
  if (props.validationRules) {
    return toTypedSchema(props.validationRules)
  }
  return undefined
})

const { handleChange, errorMessage } = useField(
  name,
  validationRulesTypedSchema.value,
  {},
)

onMounted(() => {
  currentRating.value = model.value
})

// Watch for changes to the model value and update currentRating
watch(model, (newValue) => {
  currentRating.value = newValue
  // Announce to screen readers
  announceToScreenReader(
    `Rating changed to ${newValue} out of ${props.maxRating} stars`,
  )
})

function starFill(starId: number) {
  return starId <= currentRating.value ? props.activeColor : props.inactiveColor
}

function selectRating(rating: number) {
  if (!props.readOnly) {
    model.value = rating
    handleChange(rating)
    emit('rating-selected', rating)
  }
}

function handleRatingChange(rating: number) {
  if (!props.readOnly) {
    handleChange(rating)
    emit('rating-selected', rating)
  }
}

function resetRating() {
  currentRating.value = model.value
}

function handleMouseEnter(rating: number) {
  // Only handle mouse events if not a touch device
  if (!isTouch.value) {
    currentRating.value = rating
  }
}

function handleTouchStart() {
  isTouch.value = true
  touchMoved = false
}

function handleTouchMove() {
  // Mark that the touch has moved - this indicates a scroll gesture
  touchMoved = true
}

function handleTouchEnd(rating: number) {
  // Only select rating if this was a pure tap with no movement
  // If touchMoved is true, it means the user was scrolling
  if (!touchMoved) {
    selectRating(rating)
  } else {
    // Reset preview if it was a scroll
    currentRating.value = model.value
  }
  touchMoved = false
}

function handleTouchCancel() {
  // Cancel any pending touch-based selection and reset preview
  touchMoved = false
  currentRating.value = model.value
}

// Helper function to announce changes to screen readers
let announcementTimeoutId: ReturnType<typeof setTimeout> | null = null

function announceToScreenReader(message: string) {
  if (import.meta.client) {
    const announcement = document.createElement('div')
    announcement.setAttribute('role', 'status')
    announcement.setAttribute('aria-live', 'polite')
    announcement.className = 'sr-only'
    announcement.textContent = message
    document.body.appendChild(announcement)

    if (announcementTimeoutId) {
      clearTimeout(announcementTimeoutId)
    }
    announcementTimeoutId = setTimeout(() => {
      if (document.body.contains(announcement)) {
        document.body.removeChild(announcement)
      }
    }, 1000)
  }
}

// Keyboard navigation
onMounted(() => {
  if (import.meta.client && !props.readOnly) {
    const wrapper = document.querySelector(
      `[role="radiogroup"][aria-label*="${props.name}"]`,
    )
    if (wrapper) {
      wrapper.addEventListener('keydown', handleKeyboard as EventListener)
    }
  }
})

onUnmounted(() => {
  if (announcementTimeoutId) {
    clearTimeout(announcementTimeoutId)
    announcementTimeoutId = null
  }
  if (import.meta.client && document) {
    const wrapper = document.querySelector(
      `[role="radiogroup"][aria-label*="${props.name}"]`,
    )
    if (wrapper && handleKeyboard) {
      wrapper.removeEventListener('keydown', handleKeyboard as EventListener)
    }
  }
})

function handleKeyboard(event: KeyboardEvent) {
  if (props.readOnly) return

  let newRating = model.value

  switch (event.key) {
    case 'ArrowLeft':
    case 'ArrowDown':
      event.preventDefault()
      newRating = Math.max(1, (model.value || 1) - 1)
      break
    case 'ArrowRight':
    case 'ArrowUp':
      event.preventDefault()
      newRating = Math.min(props.maxRating, (model.value || 0) + 1)
      break
    case 'Home':
      event.preventDefault()
      newRating = 1
      break
    case 'End':
      event.preventDefault()
      newRating = props.maxRating
      break
    default:
      // Handle number keys 1-9
      const num = parseInt(event.key)
      if (!isNaN(num) && num >= 1 && num <= props.maxRating) {
        event.preventDefault()
        newRating = num
      } else {
        return
      }
  }

  selectRating(newRating)
  currentRating.value = newRating

  // Focus the corresponding radio input
  const input = document.getElementById(
    `${props.name}-star-${newRating}`,
  ) as HTMLInputElement
  if (input) {
    input.focus()
  }
}
</script>

<style scoped>
.star-rating {
  display: block;
  white-space: nowrap;
}

.star-rating-input {
  height: 1px;
  left: -10000px;
  overflow: hidden;
  position: absolute;
  width: 1px;
}

.star-rating-star {
  background: none;
  border: none;
  box-sizing: border-box;
  cursor: inherit;
  display: inline-block;
  font: inherit;
  margin: 0 -2px 0 0;
  padding: 0;
  text-align: inherit;
  vertical-align: baseline;
  touch-action: pan-y;
}

.star-rating-pointer {
  cursor: pointer;
}

.star-rating-wrapper {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
}

.sr-only {
  height: 1px;
  left: -10000px;
  overflow: hidden;
  position: absolute;
  top: auto;
  width: 1px;
}
</style>
